import { Dictionary } from 'lodash';
import { Injectable } from "@angular/core";
import { Store } from "@ngrx/store";
import { AppState } from "../app.state";
import { IClubLobbyState, IStateClubGame } from "./clubLobby.state";
import { doc, docData, DocumentReference, Firestore } from "@angular/fire/firestore";
import { IClubGame, IClubState, TableType } from "@soposopo/sopo-models";
import { combineLatest, Observable, Subscription } from "rxjs";
import { distinctUntilChanged, filter, map } from "rxjs/operators";
import { removeGameTable, removeUnionClubGames, removeUnionGameTable, resetGamesState, setIsUnionSelected, setSelectedFilter, updateGameTable, updateUnionGameTable } from "./clubLobby.actions";

@Injectable({
    providedIn: 'root'
})
export class ClubLobbyDispatcher {
    private unionSubscription: Subscription;
    private unionClubSubscriptions: {[key: string]: Subscription} = {};
    private clubGamesSubscription: Subscription;
    private showUnionGames: boolean = false;
    private _subscriptionSessionTime = 0;

    constructor(
        private store: Store<AppState>,
        private firestore: Firestore,
    ) {}

    public subscribeToClubState(clubId: string, sessionTime?: number): void {
        if (sessionTime) {
            this._subscriptionSessionTime = sessionTime;
        }
        if ((this.clubGamesSubscription && !this.clubGamesSubscription.closed) || (this.unionSubscription && !this.unionSubscription.closed)) {
            this.destroySubscriptionsAndResetState();
        }

        const clubState$ = this.store.select('club')
        .pipe(
            filter(club => club.id === clubId),
            map(club => {
                return {
                    games: club.clubData.games,
                    unions: club.clubData.unions,
                }
            }),
        );

        this.store.select('clubLobby').subscribe(clubLobbyState => {
            this.showUnionGames = clubLobbyState.isUnionSelected;
        })

        this.clubGamesSubscription = this.createClubGamesSubscription(false, clubId, clubState$);
        this.unionSubscription = this.createUnionSubsription(clubState$);
    }

    private createClubGamesSubscription(isUnion: boolean, clubId: string, clubState$?: Observable<{games: Dictionary<IClubGame>, unions: {}}>): Subscription {
        const docReference = doc(this.firestore, `clubs/${clubId}`) as DocumentReference<IClubState>;

        const doc$ = docData(docReference).pipe(map(data => data?.games), distinctUntilChanged());

        const clubLobby$ = this.store.select('clubLobby');
        if (isUnion) {
            return combineLatest([doc$, clubLobby$]).subscribe(([games, clubLobbyState]) => {
                this.handleGames(true, clubId, games, clubLobbyState);
            })
        } else {
            const clubStateGames$ = clubState$.pipe(map(data => data.games), distinctUntilChanged());
            return combineLatest([clubStateGames$, clubLobby$]).subscribe(([games, clubLobbyState]) => {
                this.handleGames(false, clubId, games, clubLobbyState);
            })
        }
    }

    private handleGames(isUnion: boolean, clubId: string, games: Dictionary<IClubGame>, clubLobbyState: IClubLobbyState) {
        if (!games) return;
        const gamesFromDoc = Object.keys(games);

        let gamesInClubLobbyState: string[] = [];
        if (!isUnion) gamesInClubLobbyState = Object.keys(clubLobbyState.games);
        else if (isUnion && clubLobbyState.unionGames[clubId]) gamesInClubLobbyState = Object.keys(clubLobbyState.unionGames[clubId]);

        if (gamesFromDoc.length === gamesInClubLobbyState.length) return;

        if (gamesFromDoc.length > gamesInClubLobbyState.length) {
            let incomingGames: string[] = [];
            gamesFromDoc.forEach(id => {
                if (!gamesInClubLobbyState.includes(id)) incomingGames.push(id);
            });

            incomingGames.forEach(id => {
                const game: IStateClubGame = {
                    ...games[id],
                    clubId,
                }
                if (isUnion) {
                    this.store.dispatch(updateUnionGameTable({clubId, tableId: id, game}));
                } else {
                    this.store.dispatch(updateGameTable({game: game}));
                }
            })
        } else {
            let removedGames: string[] = [];
            gamesInClubLobbyState.forEach(id => {
                if (!gamesFromDoc.includes(id)) removedGames.push(id)
            });

            removedGames.forEach(id => {
                if (isUnion) {
                    this.store.dispatch(removeUnionGameTable({clubId, tableId: id}));
                } else {
                    this.store.dispatch(removeGameTable({tableId: id}));
                }
            })
        }
    }

    private createUnionSubsription(clubState$: Observable<{ unions: {} }>): Subscription {
        return clubState$
            .pipe(
                map(state => Object.keys(state.unions)),
                filter((unions) => unions.length !== 0),
                distinctUntilChanged((prev, current) => prev.length === current.length),
            ).subscribe(unions => {
                const unionKeys = unions;
                const currentUnionSubscriptions = Object.keys(this.unionClubSubscriptions);
                let notSubscribedUnions: string[] = [];
                let unneededSubscriptionKeys: string[] = [];
                if (unionKeys.length > currentUnionSubscriptions.length) {
                    unionKeys.forEach(id => {
                        if (!currentUnionSubscriptions.includes(id)) {
                            notSubscribedUnions.push(id);
                        }
                    });
                } else if (unionKeys.length < currentUnionSubscriptions.length) {
                    Object.keys(currentUnionSubscriptions).forEach((key) => {
                        if(!unionKeys.includes(key)) {
                            unneededSubscriptionKeys.push(key);
                        }
                    });
                }

                if (notSubscribedUnions.length) {
                    notSubscribedUnions.forEach(id => {
                        this.unionClubSubscriptions[id] = this.createClubGamesSubscription(true, id);
                    });
                }

                if (unneededSubscriptionKeys.length > 0) {
                    unneededSubscriptionKeys.forEach(id => {
                        this.unionClubSubscriptions[id].unsubscribe();
                        delete this.unionClubSubscriptions[id];
                        this.store.dispatch(removeUnionClubGames({ clubId: id }));
                    });
                }

            });
    }

    public getGamesFromClubLobby(): Observable<IStateClubGame[]> {
        return this.store.select('clubLobby')
            .pipe(
                map(clubLobbyState => {
                    switch (clubLobbyState.selectedFilter) {
                        case 'Ring':
                            return this.getGamesByType(clubLobbyState, TableType.RING);
                        case TableType.SNG:
                            return this.getGamesByType(clubLobbyState, TableType.SNG);
                        case TableType.MTT:
                            return this.getGamesByType(clubLobbyState, TableType.MTT);
                        default:
                            return this.getSortedGameList(clubLobbyState);
                    }
                }),
                distinctUntilChanged(),
            )
    }

    private sortGameByType(games: IStateClubGame[]): IStateClubGame[] {
        return games.sort((a, b) => {
            const aTime = a.dateCreated?.seconds ?? Infinity;
            const bTime = b.dateCreated?.seconds ?? Infinity;
            return aTime - bTime;
        });
    }

    private getGamesByType(clubLobbyState: IClubLobbyState, type: TableType): IStateClubGame[] {
        if (this.showUnionGames) {
            const clubIds = Object.keys(clubLobbyState.unionGames);
            let allUnionGames: IStateClubGame[] = [];
            clubIds.forEach(clubId => {
                Object.entries(clubLobbyState.unionGames[clubId]).map(entry => {
                    allUnionGames.push(entry[1]);
                })
            })
            const unionGames = allUnionGames.filter(v => v.type === type);
            const clubGames = Object.values(clubLobbyState.games).filter(v => v.type === type);
            return [...this.sortGameByType(clubGames), ...this.sortGameByType(unionGames)];
        }
        return Object.values(clubLobbyState.games).filter(v => v.type === type);
    }

    private getSortedGameList(clubLobbyState: IClubLobbyState): IStateClubGame[] {
        let unionGames: IStateClubGame[] = [];
        if (this.showUnionGames) {
            const clubIds = Object.keys(clubLobbyState.unionGames);
            let ringUnionGames: IStateClubGame[] = [];
            let sngUnionGames: IStateClubGame[] = [];
            let mttUnionGames: IStateClubGame[] = [];
            clubIds.forEach(clubId => {
                Object.entries(clubLobbyState.unionGames[clubId]).map(entry => {
                    if (entry[1].type === TableType.RING) ringUnionGames.push(entry[1]);
                    if (entry[1].type === TableType.SNG) sngUnionGames.push(entry[1]);
                    if (entry[1].type === TableType.MTT) mttUnionGames.push(entry[1]);
                })
            })
            unionGames = [...this.sortGameByType(ringUnionGames), ...this.sortGameByType(sngUnionGames), ...this.sortGameByType(mttUnionGames)];
        }
        let ringGames: IStateClubGame[] = [];
        let sngGames: IStateClubGame[] = [];
        let mttGames: IStateClubGame[] = [];
        Object.entries(clubLobbyState.games).map(entry => {
            if (entry[1].type === TableType.RING) ringGames.push(entry[1]);
            if (entry[1].type === TableType.SNG) sngGames.push(entry[1]);
            if (entry[1].type === TableType.MTT) mttGames.push(entry[1]);
        })
        return [...this.sortGameByType(ringGames), ...this.sortGameByType(sngGames), ...this.sortGameByType(mttGames), ...unionGames];
    }

    public setSelectedFilter(selectedFilter: string): void {
        this.store.dispatch(setSelectedFilter({ selectedFilter }));
    }

    public setIsUnionSelected(isUnionSelected: boolean) {
        this.store.dispatch(setIsUnionSelected({ isUnionSelected }));
    }

    public getSelectedFilterState(): Observable<string> {
        return this.store.select('clubLobby').pipe(
            map(clubLobbyState => clubLobbyState.selectedFilter)
        );
    }

    public destroySubscriptionsAndResetState(sessionTime?: number): void {
        if (sessionTime && sessionTime < this._subscriptionSessionTime) {
            return;
        }
        if (this.clubGamesSubscription) this.clubGamesSubscription.unsubscribe();
        if (this.unionSubscription) this.unionSubscription.unsubscribe();
        const unionKeys = Object.keys(this.unionClubSubscriptions);
        for (let unionKey of unionKeys) {
            this.unionClubSubscriptions[unionKey].unsubscribe();
        }
        this.unionClubSubscriptions = {};
        this.store.dispatch(resetGamesState());
    }
}
